import * as Sentry from '@sentry/react';
import { TRPCClientError } from '@trpc/client';
import { posthog } from 'posthog-js';
import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { useLocalStorage } from 'usehooks-ts';
import { LoadingBlockFullScreen } from '../../../components/loading/loading-block-full-screen.tsx';
import { IntercomProvider } from '../../../components/providers/intercom-provider.tsx';
import { useImpersonation } from '../../../lib/hooks/use-impersonation.ts';
import { useSignOut } from '../../../lib/hooks/use-sign-out.ts';
import { cn } from '../../../lib/utils.ts';
import { api } from '../../../utils/api.ts';
import { getFullName } from '../../../utils/name.ts';
import type { PlatoUserMe } from '../../../utils/plato-user.ts';
import { AclProvider } from '../../acl/acl-provider.tsx';
import { CatalogProvider } from '../../catalog/components/global-catalog-provider.tsx';
import { DialogProvider } from '../../dialog/dialog-provider.tsx';
import { AllDrawers, AllDrawersProvider } from '../../drawers/all-drawers.tsx';
import { VersionUpdateToast } from '../../version-watch/version-update-toast.tsx';
import { useIsManagerView, useManagerViewEnabled } from '../hooks/use-view-mode.ts';
import { FullWidthNav } from './full-width-nav.tsx';
import { GlobalSearchBarProvider } from './global-search-bar-provider.tsx';
import { IconOnlyNav } from './icon-only-nav.tsx';
import { MobileBottomNav } from './mobile-bottom-nav.tsx';
import { MobileTopBar } from './mobile-top-bar.tsx';
import { ViewModePageGuard } from './view-mode-page-guard.tsx';
export { NavPathOverrideEnum } from '../contracts.ts';

export const PlatoUserContext = createContext<PlatoUserMe | undefined>(undefined);

export type RouteHandle = {
    disableDrawers?: boolean;
};

const PlatoUserLayoutImplementation = (): ReactNode => {
    const [smallScreenDrawOpen, setSmallScreenDrawOpen] = useState(false);
    const isManagerView = useIsManagerView();
    const managerViewEnabled = useManagerViewEnabled();

    // View-mode chrome policy, decided once here and passed down so the nav
    // variants just render: the view-mode switch shows whenever the manager
    // experience is enabled; the rep-oriented "Create new" is hidden in Manager
    // view.
    const showViewSwitch = managerViewEnabled;
    const showCreateNew = !isManagerView;

    const [desktopSidebarOpen, setDesktopSidebarOpen] = useLocalStorage<boolean>(
        'desktop-sidebar-open',
        true,
    );

    const closeDrawer = () => {
        setSmallScreenDrawOpen(false);
    };

    // Close mobile drawer on orientation change
    useEffect(() => {
        const orientation = screen.orientation;

        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- This is legacy code, if you know what are you doing, please fix me
        if (!orientation) {
            return;
        }

        const handleOrientationChange = () => {
            closeDrawer();
        };

        orientation.addEventListener('change', handleOrientationChange);
        return () => {
            orientation.removeEventListener('change', handleOrientationChange);
        };
    }, []);

    return (
        <GlobalSearchBarProvider
            onBeforeOpenSearch={closeDrawer}
            needsToCloseOtherWindowBeforeOpeningSearch={smallScreenDrawOpen}
        >
            <div className="z-30 h-full">
                {/* Side navigation bars */}
                <div className="fixed z-30 hidden h-full w-[52px] border-r border-neutral-200 bg-neutral-50 sm:inset-y-0 sm:left-0 sm:block xl:hidden">
                    <IconOnlyNav
                        isDesktopScreenSize={false}
                        smallScreenDrawOpen={smallScreenDrawOpen}
                        setSmallScreenDrawOpen={setSmallScreenDrawOpen}
                        setDesktopSidebarOpen={setDesktopSidebarOpen}
                        closeDrawer={closeDrawer}
                        showCreateNew={showCreateNew}
                        showViewSwitch={showViewSwitch}
                    />
                </div>
                {/* full desktop sidebar */}
                <div
                    className={cn(
                        'z-30 hidden h-full border-r border-neutral-200 bg-neutral-50 xl:fixed xl:inset-y-0 xl:left-0 xl:block',
                        desktopSidebarOpen ? 'xl:w-[250px]' : 'xl:w-[52px]',
                    )}
                >
                    {desktopSidebarOpen ? (
                        <FullWidthNav
                            isDesktopScreenSize={true}
                            closeDrawer={closeDrawer}
                            setSmallScreenDrawOpen={setSmallScreenDrawOpen}
                            setDesktopSidebarOpen={setDesktopSidebarOpen}
                            showCreateNew={showCreateNew}
                            showViewSwitch={showViewSwitch}
                        />
                    ) : (
                        <IconOnlyNav
                            isDesktopScreenSize={true}
                            smallScreenDrawOpen={smallScreenDrawOpen}
                            setSmallScreenDrawOpen={setSmallScreenDrawOpen}
                            setDesktopSidebarOpen={setDesktopSidebarOpen}
                            closeDrawer={closeDrawer}
                            showCreateNew={showCreateNew}
                            showViewSwitch={showViewSwitch}
                        />
                    )}
                </div>
                {/* Mobile bottom nav outside the z-0 stacking context so backdrop-filter works */}
                <MobileBottomNav />
                <div
                    className={cn(
                        'z-0 flex h-dvh max-h-dvh flex-col pl-0 sm:pl-[52px]',
                        desktopSidebarOpen ? 'xl:pl-[250px]' : 'xl:pl-[52px]',
                    )}
                >
                    {/* Mobile top bar (no hamburger — nav lives in bottom bar) */}
                    <MobileTopBar showCreateNew={showCreateNew} />
                    <div className="flex grow min-h-0 flex-col lg:rounded-tl-lg">
                        <Outlet />
                    </div>
                </div>
            </div>
            <AllDrawers />
        </GlobalSearchBarProvider>
    );
};

export const PlatoUserLayout = (): ReactNode => {
    const navigate = useNavigate();
    const { signOutFn } = useSignOut();
    const [impersonatedUserId] = useImpersonation();
    const isImpersonating = impersonatedUserId !== undefined;

    const {
        data: platoUser,
        error,
        isLoading,
    } = api.platoUser.me.getMe.useQuery(undefined, {
        // cache result for a short time to reduce requests
        staleTime: 60_000,
        // don't retry expected errors
        retry: (_, error) => error.data?.code !== 'FORBIDDEN' && error.data?.code !== 'NOT_FOUND',
    });

    const { data: superAdmin } = api.superAdmin.me.getMe.useQuery(undefined, {
        staleTime: 60_000 * 5,
        enabled: isImpersonating,
    });

    useEffect(() => {
        if (!error || !(error instanceof TRPCClientError)) {
            return;
        }
        const data: unknown = error.data;
        if (typeof data !== 'object' || data === null || !('code' in data)) {
            return;
        }
        if (data.code === 'FORBIDDEN') {
            void navigate('/admin', { replace: true });
        } else if (data.code === 'NOT_FOUND') {
            signOutFn();
        }
    }, [error, navigate, signOutFn]);

    useEffect(() => {
        if (!platoUser || process.env.NODE_ENV === 'development') {
            return;
        }

        if (isImpersonating && superAdmin) {
            Sentry.setUser({
                id: superAdmin.id,
                email: superAdmin.email,
                name: getFullName(superAdmin.firstName, superAdmin.lastName),
                role: 'SUPER_ADMIN',
            });
            Sentry.setContext('Impersonation', {
                email: platoUser.email,
                id: platoUser.id,
                role: platoUser.role,
            });

            Sentry.setTags({
                impersonation: true,
            });
        } else {
            Sentry.setUser({
                id: platoUser.id,
                email: platoUser.email,
                name: getFullName(platoUser.firstName, platoUser.lastName),
                role: platoUser.role,
            });
        }

        Sentry.setTag('tenantId', platoUser.platoCustomer.id);
        Sentry.setTag('tenantSlug', platoUser.platoCustomer.slug);
        Sentry.setTag('userRole', platoUser.role);
        Sentry.setTag('impersonation', isImpersonating);
    }, [platoUser, isImpersonating, superAdmin]);

    useEffect(() => {
        if (process.env.NODE_ENV === 'development') {
            return;
        }

        if (isImpersonating && superAdmin) {
            posthog.identify(superAdmin.email, {
                IS_SUPER_ADMIN: true,
                id: superAdmin.id,
                firstName: superAdmin.firstName,
                lastName: superAdmin.lastName,
                name: getFullName(superAdmin.firstName, superAdmin.lastName),
                email: superAdmin.email,
            });
            posthog.group('plato_customer', 'id:plato_team_superuser', {
                customer_id: 'Plato Team',
                customer_name: 'Plato Team',
            });
            return;
        }

        if (!isImpersonating && platoUser) {
            posthog.identify(platoUser.email, {
                IS_SUPER_ADMIN: false,
                id: platoUser.id,
                firstName: platoUser.firstName,
                lastName: platoUser.lastName,
                name: getFullName(platoUser.firstName, platoUser.lastName),
                customer_id: platoUser.platoCustomer.id,
                customer_name: platoUser.platoCustomer.name,
                customer_slug: platoUser.platoCustomer.slug,
                erpSalesRepId: platoUser.erpSalesRepId,
                salesRepIds: platoUser.salesRepIds,
                showDataForErpSalesRepId: platoUser.showDataForErpSalesRepId,
                email: platoUser.email,
                role: platoUser.role,
                description: platoUser.descriptionString,
            });
            posthog.group('plato_customer', `id:${platoUser.platoCustomer.id}`, {
                customer_id: platoUser.platoCustomer.id,
                customer_name: platoUser.platoCustomer.name,
                customer_slug: platoUser.platoCustomer.slug,
            });
        }
    }, [platoUser, isImpersonating, superAdmin]);

    if (isLoading || !platoUser) {
        return <LoadingBlockFullScreen />;
    }

    return (
        <PlatoUserContext.Provider value={platoUser}>
            <IntercomProvider>
                <AclProvider>
                    <VersionUpdateToast />
                    <ViewModePageGuard>
                        <AllDrawersProvider>
                            <DialogProvider>
                                <CatalogProvider>
                                    <PlatoUserLayoutImplementation />
                                </CatalogProvider>
                            </DialogProvider>
                        </AllDrawersProvider>
                    </ViewModePageGuard>
                </AclProvider>
            </IntercomProvider>
        </PlatoUserContext.Provider>
    );
};

export function usePlatoUserContext(): PlatoUserMe {
    const ctx = useContext(PlatoUserContext);
    if (!ctx) {
        throw new Error('usePlatoUserContext must be used within <PlatoUserLayout/>');
    }
    return ctx;
}
